// LOCAL v12 experiment: native video shows front-loaded motion, not linear.
// Keep the inherited 408ms clock and 80ms reconciliation/source policy.
// Quintic is an app-only approximation, NOT a universal OS curve or native PASS.
const SIGNAL_INTERVAL_MS = 408;
export function sheetViewportMotion(host: HTMLElement, panel: HTMLElement, readHost: () => number, changed: (height: number) => void) {
  const visual = window.visualViewport;
  let shown = Math.min(visual?.height ?? innerHeight, readHost() || innerHeight);
  let target = shown, from = shown, started = 0, frame = 0, duration = SIGNAL_INTERVAL_MS;
  let lastHost = readHost(), lastVisual = visual?.height ?? innerHeight, lastLayout = innerHeight;
  let pendingBrowser: number | null = null;
  let pendingAt = 0, reconciling = false;
  let width = innerWidth, hostUntil = 0, disposed = false;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Freeze this sheet's safe-area reservation, rather than letting a late
  // root viewport update independently resize its footer mid-transition.
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;visibility:hidden;padding-bottom:var(--app-safe-bottom,0px)';
  host.append(probe);
  host.style.setProperty('--app-safe-bottom', getComputedStyle(probe).paddingBottom);
  probe.remove();
  host.dataset.sheetViewport = 'experimental-v10';
  const paint = () => {
    host.style.setProperty('--sheet-height', `${shown}px`);
    // Fixed geometry is in the layout viewport. offsetTop during host-driven
    // opening is transient native caret pan, not a second desired origin.
    const top = performance.now() < hostUntil ? 0 : visual?.scale === 1 ? visual.offsetTop : 0;
    host.style.setProperty('--sheet-top', `${top}px`);
    changed(target);
  };
  const cancel = () => { cancelAnimationFrame(frame); frame = 0; };
  // Reversals sample the same primary curve; the bounded handoff stays linear.
  const position = (progress: number) => from + (target - from) * (reconciling ? progress : 1 - (1 - progress) ** 5);
  const tick = (now: number) => {
    if (disposed) return;
    const progress = Math.min(1, Math.max(0, (now - started) / duration));
    shown = position(progress);
    paint();
    if (progress >= 1 && pendingBrowser !== null) {
      // Reconcile once, smoothly, in the grace rather than snapping at expiry.
      // A late first sample gets at most 80ms; further samples cannot extend it.
      from = shown; target = pendingBrowser; pendingBrowser = null;
      started = Math.max(started + duration, pendingAt); duration = 80;
      hostUntil = started + duration; reconciling = true;
      tick(now); return;
    }
    frame = progress < 1 || now < hostUntil ? requestAnimationFrame(tick) : 0;
    if (!frame) reconciling = false;
  };
  const direct = (height: number) => { cancel(); reconciling = false; target = shown = height; paint(); };
  const sample = (source: 'host' | 'browser' = 'browser') => {
    const now = performance.now(), h = readHost(), v = visual?.scale === 1 ? visual.height : innerHeight;
    const layoutChanged = innerHeight !== lastLayout;
    const browserHeight = layoutChanged && v === lastVisual ? innerHeight : v;
    lastLayout = innerHeight;
    if (innerWidth !== width || visual && visual.scale !== 1) {
      width = innerWidth; hostUntil = 0; pendingBrowser = null; lastHost = h; lastVisual = v;
      direct(v); return;
    }
    // 'host' is an actual viewportChanged callback, never a browser poll.
    // After browser recovery, a fresh callback can reclaim the same endpoint;
    // equal stable callbacks during its active clock must not restart it.
    if (source === 'host' && h > 0 && (Math.abs(h - lastHost) > 1 || !frame && Math.abs(h - target) > 1)) {
      lastHost = h; lastVisual = v; pendingBrowser = null; hostUntil = now + SIGNAL_INTERVAL_MS + 80;
      // Cancel only the CSS entrance, never the swipe-owned transform.
      for (const animation of panel.getAnimations()) {
        if (animation instanceof CSSAnimation && animation.animationName === 'rise') animation.cancel();
      }
      if (frame) { cancel(); const p = Math.min(1, (now - started) / duration); shown = position(p); }
      from = shown; target = h; started = now; duration = SIGNAL_INTERVAL_MS; reconciling = false;
      if (reduced.matches) { hostUntil = 0; direct(h); }
      else { paint(); frame = requestAnimationFrame(tick); }
      return;
    }
    // A concordant fractional browser endpoint confirms, not a new animation.
    if (Math.abs(browserHeight - target) <= 1) {
      lastVisual = v;
      pendingBrowser = null;
      if (!frame) paint();
      return;
    }
    if (v !== lastVisual || layoutChanged) {
      lastVisual = v;
      // Ignore stale / behind-the-clock browser samples during early-host
      // interpolation. No document-wide 'ever responsive' source ownership.
      if (now >= hostUntil) { pendingBrowser = null; direct(browserHeight); }
      else if (reconciling) {
        const p = Math.min(1, (now - started) / duration);
        shown = position(p);
        from = shown; target = browserHeight; started = now;
        duration = hostUntil - now; // Fixed deadline: never a restart loop.
      } else {
        pendingBrowser = browserHeight; pendingAt = now;
        if (now >= started + duration) { cancel(); tick(now); }
      }
    } else if (!frame) paint();
  };
  const pagehide = () => { cancel(); };
  const motionPreference = () => { if (reduced.matches) direct(target); };
  window.addEventListener('pagehide', pagehide);
  reduced.addEventListener('change', motionPreference);
  paint();
  return {sample, dispose() { disposed = true; cancel(); window.removeEventListener('pagehide', pagehide); reduced.removeEventListener('change', motionPreference); host.style.removeProperty('--app-safe-bottom'); delete host.dataset.sheetViewport; }};
}
